import React, { useEffect, useState } from "react";
import { appPrimaryColor, appColors } from "../utils/constants";
import InnerLoader from "../components/microComponents/InnerLoader";
import RightArrow from "../assets/svg/RightArrow";
import { getSpecificOrgList, deleteOrganization } from "../api";
import { toast } from "react-toastify";
import AddUserModal from "../components/modal/AddUserModal";
import { BiSearch } from "react-icons/bi";
import { IoMdAdd } from "react-icons/io";
import { AiOutlineDelete } from "react-icons/ai";
import FarmerInviteStatusButton from "../components/microComponents/FarmerInviteStatusButton";

const Organizations = () => {
    const [searchValue, setSearchValue] = useState("");
    const [organizationsList, setOrganizationsList] = useState([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [loader, setLoader] = useState(false);
    const [detailsPageNumber, setDetailsPageNumber] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [orgToDelete, setOrgToDelete] = useState(null);

    const openModal = () => {
        setIsModalOpen(true)
        setSearchValue('')
    };
    const closeModal = () => {
        setIsModalOpen(false)
        setSearchValue('')
        setDetailsPageNumber(1)
        setLoader(true)
        getOrganizationDetails()
    };

    const getOrganizationDetails = async () => {
        try {
            const response = await getSpecificOrgList(searchValue, detailsPageNumber);
            console.log("🚀 ~ getOrganizationDetails ~ response:", response)
            let updatedOrgList = response?.invite_details;
            // Filter according to the repeated email
            updatedOrgList = updatedOrgList?.filter((org, index, self) =>
                index === self.findIndex((t) => t.email === org.email)
            );
            setOrganizationsList(updatedOrgList ?? []);
            setTotalPages(response?.total_pages ?? 1);
        } catch (error) {
            console.error("Error fetching organization details:", error);
            toast.error("Error fetching organization details");
        } finally {
            setLoader(false);
        }
    };

    const [debounceTimer, setDebounceTimer] = useState(0);

    const debounce = (callback, delay = 400) => {
        if (debounceTimer) clearTimeout(debounceTimer);
        setDebounceTimer(setTimeout(callback, delay));
    };

    useEffect(() => {
        setLoader(true);
        debounce(() => getOrganizationDetails(), 700);
    }, [detailsPageNumber, searchValue]);

    useEffect(() => {
        setDetailsPageNumber(1);
    }, [searchValue]);

    const handleDeleteOrg = (org) => {
        setOrgToDelete(org);
        setIsDeleteModalOpen(true);
    };

    const confirmDelete = () => {
        setLoader(true);
        deleteOrganization(orgToDelete?._id)
            .then((response) => {
                setIsDeleteModalOpen(false);
                setOrgToDelete(null);
                if (response.status === 200) {
                    toast.success("Organization deleted successfully");
                    getOrganizationDetails();
                } else {
                    toast.error("Error deleting organization");
                    setLoader(false);
                }
            })
            .catch((error) => {
                console.error("Error deleting organization:", error);
                toast.error("Error deleting organization");
                setLoader(false);
            });
    };

    return (
        <div className="flex flex-col gap-6 w-full max-w-[1400px] mx-auto">
            {/* Header Section */}
            <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 bg-white p-6 rounded-xl shadow-sm">
                <div>
                    <h1 className="text-2xl font-bold text-gray-800">Organizations Management</h1>
                    <p className="text-gray-500 mt-1">Manage and monitor your organization network</p>
                </div>
                <button
                    onClick={openModal}
                    className={`${appPrimaryColor} px-6 py-2.5 text-white rounded-lg hover:opacity-90 transition-all flex items-center gap-2`}
                >
                    <IoMdAdd size={20} />
                    <span>Add Organization</span>
                </button>
            </div>

            {/* Main Content */}
            <div className="bg-white rounded-xl shadow-sm p-6">
                {/* Search and Filters */}
                <div className="flex flex-col lg:flex-row gap-4 justify-between items-start lg:items-center mb-6">
                    <div className="relative w-full lg:w-96 flex flex-row justify-center items-center gap-4">
                        <BiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                        <input
                            type="search"
                            className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-200 focus:outline-none focus:border-darkGreen"
                            placeholder="Search organizations..."
                            value={searchValue}
                            onChange={(e) => setSearchValue(e.target.value)}
                        />
                        {loader && <InnerLoader loaderSize={{ width: "30px", height: "30px" }}
                            divSize={{ width: "30px", height: "30px" }}
                        />}
                    </div>

                    {/* Pagination Controls */}
                    <div className="flex items-center gap-2 text-sm">
                        <button
                            onClick={() => setDetailsPageNumber(prev => prev === 1 ? 1 : prev - 1)}
                            className="p-2 rounded-lg border hover:bg-gray-50 transition-colors disabled:opacity-50"
                            disabled={detailsPageNumber === 1}
                        >
                            <RightArrow width={14} height={14} className="rotate-180" />
                        </button>
                        <span className="px-4 py-2 border rounded-lg">
                            Page {detailsPageNumber} of {totalPages}
                        </span>
                        <button
                            onClick={() => detailsPageNumber < totalPages ? setDetailsPageNumber(prev => prev + 1) : null}
                            className="p-2 rounded-lg border hover:bg-gray-50 transition-colors disabled:opacity-50"
                            disabled={detailsPageNumber >= totalPages}
                        >
                            <RightArrow width={14} height={14} />
                        </button>
                    </div>
                </div>

                {/* Table */}
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead>
                            <tr className="bg-gray-50 text-left">
                                <th className="px-6 py-3 text-sm font-semibold text-gray-600 rounded-tl-lg">Name</th>
                                <th className="px-6 py-3 text-sm font-semibold text-gray-600 text-center">Email</th>
                                <th className="px-6 py-3 text-sm font-semibold text-gray-600 text-center">Phone</th>
                                <th className="px-6 py-3 text-sm font-semibold text-gray-600 text-center">Status</th>
                                <th className="px-6 py-3 text-sm font-semibold text-gray-600 rounded-tr-lg text-center">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {loader ? (
                                <tr>
                                    <td colSpan="5" className="text-center py-8"></td>
                                </tr>
                            ) : organizationsList?.length === 0 ? (
                                <tr>
                                    <td colSpan="5" className="text-center py-8 text-gray-500">
                                        No organizations found
                                    </td>
                                </tr>
                            ) : (
                                organizationsList?.length > 0 && organizationsList?.map((organization, index) => (
                                    <tr key={index} className="hover:bg-gray-50 transition-colors">
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-3">
                                                <div className="w-8 h-8 rounded-full bg-darkGreen text-white flex items-center justify-center uppercase">
                                                    {organization?.org_name?.[0] ?? '-'}
                                                </div>
                                                <span className="font-medium">
                                                    {organization?.org_name ?? '-'}
                                                </span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 text-gray-600 text-center">{organization?.email ?? '-'}</td>
                                        <td className="px-6 py-4 text-gray-600 text-center">{organization?.phone ? organization?.phone : '-'}</td>
                                        <td className="px-6 py-4 flex items-center justify-center"
                                        >
                                            <FarmerInviteStatusButton status={organization?.status ?? '-'} barHeight={'h-6'} />
                                        </td>
                                        <td className="py-4 text-center">
                                            <button
                                                className="text-red-500 hover:text-red-700 transition-colors p-2 rounded-full hover:bg-red-50"
                                                onClick={() => handleDeleteOrg(organization)}
                                            >
                                                <AiOutlineDelete size={20} color={appColors.secondaryRed} />
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            <AddUserModal isOpen={isModalOpen} onRequestClose={closeModal} admin={true} />

            {/* Delete Confirmation Modal */}
            {isDeleteModalOpen && (
                <div className="fixed inset-0 z-50 overflow-y-auto">
                    <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" />

                    <div className="flex min-h-full items-center justify-center p-4">
                        <div className="relative w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left shadow-xl transition-all">
                            <div className="mb-6">
                                <h2 className="text-2xl font-bold text-gray-900">Confirm Delete</h2>
                                <p className="mt-2 text-sm text-gray-500">
                                    Are you sure you want to delete {orgToDelete?.org_name}? This action is irreversible. All associated data, including farmer details, questions, and invitations, will be permanently deleted.
                                </p>
                            </div>

                            <div className="flex flex-col-reverse sm:flex-row gap-3 sm:justify-end pt-4">
                                <button
                                    onClick={() => {
                                        setIsDeleteModalOpen(false);
                                        setOrgToDelete(null);
                                    }}
                                    className="w-full sm:w-auto px-4 py-2.5 rounded-lg border border-gray-300
                                        text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 
                                        focus:ring-offset-2 focus:ring-gray-500 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={confirmDelete}
                                    className={`min-w-[150px] sm:w-auto px-6 py-2.5 rounded-lg text-white  bg-secondaryRed hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors text-center ${loader ? 'opacity-50 cursor-not-allowed' : ''}`}
                                    disabled={loader}
                                >
                                    {loader ? "Deleting..." : 'Delete Organization'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Organizations; 